import { ConditionContext } from '@appcraft/common-utils';
import i18n from '../i18n/i18n';

export interface Operator {
  text: string;
  value: string;
  valueType: string;
}

const isChecked = {
  text: i18n.t('generic.condition.op.isChecked'),
  value: 'isChecked',
  valueType: 'boolean',
};

const isNotChecked = {
  text: i18n.t('generic.condition.op.isNotChecked'),
  value: 'isNotChecked',
  valueType: 'boolean',
};

const isEqual = {
  text: i18n.t('generic.condition.op.isEqual'),
  value: 'isEqual',
  valueType: 'string',
};

const isNotEqual = {
  text: i18n.t('generic.condition.op.isNotEqual'),
  value: 'isNotEqual',
  valueType: 'string',
};
const isAnyOf = {
  text: i18n.t('generic.condition.op.isAnyOf'),
  value: 'isAnyOf',
  valueType: 'array',
};
const isNoneOf = {
  text: i18n.t('generic.condition.op.isNoneOf'),
  value: 'isNoneOf',
  valueType: 'array',
};
const contains = {
  text: i18n.t('generic.condition.op.contains'),
  value: 'contains',
  valueType: 'string',
};
const doesntContain = {
  text: i18n.t('generic.condition.op.doesntContain'),
  value: 'doesntContain',
  valueType: 'string',
};
const isEmpty = {
  text: i18n.t('generic.condition.op.isEmpty'),
  value: 'isEmpty',
  valueType: 'null',
};
const isNotEmpty = {
  text: i18n.t('generic.condition.op.isNotEmpty'),
  value: 'isNotEmpty',
  valueType: 'null',
};

const hasAnyOf = {
  text: i18n.t('generic.condition.op.hasAnyOf'),
  value: 'hasAnyOf',
  valueType: 'array',
};
const hasAllOf = {
  text: i18n.t('generic.condition.op.hasAllOf'),
  value: 'hasAllOf',
  valueType: 'array',
};
const isExactly = {
  text: i18n.t('generic.condition.op.isExactly'),
  value: 'isExactly',
  valueType: 'array',
};
const hasNoneOf = {
  text: i18n.t('generic.condition.op.hasNoneOf'),
  value: 'hasNoneOf',
  valueType: 'array',
};
const has = {
  text: i18n.t('generic.condition.op.has'),
  value: 'has',
  valueType: 'string',
};
const doesntHave = {
  text: i18n.t('generic.condition.op.doesntHave'),
  value: 'doesntHave',
  valueType: 'string',
};
export const operationValues = [
  isEqual,
  isNotEqual,
  isAnyOf,
  isNoneOf,
  contains,
  doesntContain,
  isEmpty,
  isNotEmpty,
];
export const booleanValues = [isChecked, isNotChecked];
export const selectOperationValues = [isEqual, isNotEqual, isAnyOf, isNoneOf, isEmpty, isNotEmpty];
export const multiselectOperationValues = [
  has,
  doesntHave,
  hasAnyOf,
  hasAllOf,
  isExactly,
  hasNoneOf,
  isEmpty,
  isNotEmpty,
];

export const numericOperators = [
  {
    text: '>',
    value: '>',
  },
  {
    text: '≥',
    value: '>=',
  },
  {
    text: '<',
    value: '<',
  },
  {
    text: '≤',
    value: '<=',
  },
].map((v) => ({ ...v, valueType: 'number' }));

export const andOrValues = [
  {
    label: i18n.t('generic.condition.and-label'),
    value: 'and',
  },
  {
    label: i18n.t('generic.condition.or-label'),
    value: 'or',
  },
];

export const labels = {
  and: i18n.t('generic.condition.op.and'),
  or: i18n.t('generic.condition.op.or'),
};

function ensureArray(v: string | string[]) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [v];
}

const ops: Record<string, any> = {
  isEqual: { type: 'binop', printLabel: '=' },
  isNotEqual: { type: 'binop', printLabel: '≠' },
  isExactly: { type: 'arrayop', printLabel: 'IS EXACTLY' },
  isAnyOf: { type: 'arrayop', printLabel: 'IS ANY OF' },
  isNoneOf: { type: 'arrayop', printLabel: 'IS NONE OF' },
  hasAllOf: { type: 'arrayop', printLabel: 'HAS ALL OF' },
  hasAnyOf: { type: 'arrayop', printLabel: 'HAS ANY OF' },
  hasNoneOf: { type: 'arrayop', printLabel: 'HAS NONE OF' },
  contains: { type: 'binop', printLabel: 'CONTAINS' },
  doesntContain: { type: 'binop', printLabel: "DOESN'T CONTAIN" },
  has: { type: 'binop', printLabel: 'HAS' },
  doesntHave: { type: 'binop', printLabel: "DOESN'T HAVE" },
  isEmpty: { type: 'unop', printLabel: 'IS EMPTY' },
  isNotEmpty: { type: 'unop', printLabel: 'IS NOT EMPTY' },
  '>': { type: 'binop' },
  '<': { type: 'binop' },
  '>=': { type: 'binop' },
  '<=': { type: 'binop' },
  and: { type: 'boolop', printLabel: 'AND' },
  or: { type: 'boolop', printLabel: 'OR' },
};

function convertExpressionToText(expression: any, indentSize = 0): string {
  const { op, key, value, expressions } = expression;
  const { type, printLabel = op } = ops[op] || {};
  const valueLabel = Array.isArray(value) ? `[${value.join(', ')}]` : value;
  switch (type) {
    case 'binop': {
      return `${key} ${printLabel} ${valueLabel}`;
    }
    case 'unop': {
      return `${key} ${printLabel}`;
    }
    case 'arrayop': {
      return `${key} ${printLabel} [${ensureArray(value).join(', ')}]`;
    }
    case 'boolop': {
      return `(${expressions
        .map((e: any) => convertExpressionToText(e, indentSize + 2))
        .join(` \n${printLabel} `)})`;
    }
    default:
      return 'unknown';
  }
}

function removeOuterParentheses(text: string) {
  if (!text) return text;
  let str = text;
  if (str[0] === '(') str = str.slice(1);
  if (str[str.length - 1] === ')') str = str.slice(0, str.length - 1);
  return str;
}

export function convertToText(condition: ConditionContext, mustIndent = false): string {
  if (!condition) return '';
  let expString = convertExpressionToText(condition, 0);
  if (!mustIndent) expString = expString.replace(/\n/g, '').replace(/[ ]{2}/g, '');
  return removeOuterParentheses(expString);
}
